import { useEffect, useRef, useState } from "react";
import { Check, X } from "lucide-react";
import { cn } from "./ui";
import { apiErrorText } from "@/lib/api";
import { contactsApi, fieldsApi, showContact, tagsApi, type Contact, type ContactFieldDef, type ContactInput, type Stage, type Tag, type TagGroup } from "./contacts-api";
import { contactCountries, DEFAULT_COUNTRY, stageTone, type ContactOwner } from "./contactsapp-meta";
import { OwnerControl } from "./owner-control";
import { PERSON_ROLES, aed, showCompany, zohoApi, type ZohoCustomer } from "./companies-api";
import { goAnchor } from "./app-nav";
import { dealsApi, money, showDeal, showNewDeal, type Deal } from "./deals-api";
import { tagColors } from "./tags-meta";
import { NotesBlock, TasksBlock } from "./activity";
import { FinanceBlock } from "./finance";
import { ContactDialogs, WhereFrom } from "./contact-dialogs";
import { showNewDialog } from "./inbox-api";
import TagPicker from "./tag-picker";
import { t } from "@/lib/i18n";

/* Боковая панель «Новый контакт» / «Контакт»: данные контакта, страна в телефоне,
   владелец карточки и выпадающий выбор этапа жизненного цикла (обычные и потерянные этапы).
   Сохраняет сразу на сервер; телефон сервер приводит к виду +971501234567. */

const FIELD =
  "h-9 w-full box-border rounded-btn border border-border bg-background px-2.5 text-[12.5px] text-foreground placeholder:text-muted-foreground/70 focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25";

type Props = {
  mode: "new" | "edit";
  contact?: Contact;
  stages: Stage[];
  owners: ContactOwner[];
  meId: string;
  /** владелец/администратор/менеджер: может менять владельца и удалять */
  staff: boolean;
  onClose: () => void;
  onSaved: (contact: Contact, isNew: boolean) => void;
  onDeleted: (contact: Contact) => void;
};

/** разбирает сохранённый телефон на код страны и номер */
function splitPhone(phone: string | null | undefined, country: string | undefined) {
  if (!phone) return { country: country && contactCountries.some((c) => c.code === country) ? country : DEFAULT_COUNTRY.code, phone: "" };
  const own = contactCountries.find((c) => c.code === country && phone.startsWith(c.dial));
  const any = [...contactCountries].sort((a, b) => b.dial.length - a.dial.length).find((c) => phone.startsWith(c.dial));
  const hit = own ?? any;
  return hit ? { country: hit.code, phone: phone.slice(hit.dial.length) } : { country: country || DEFAULT_COUNTRY.code, phone };
}

export default function ContactPanel({ mode, contact, stages, owners, meId, staff, onClose, onSaved, onDeleted }: Props) {
  const initial = splitPhone(contact?.phone, contact?.country);
  const [first, setFirst] = useState(contact?.firstName ?? "");
  const [last, setLast] = useState(contact?.lastName ?? "");
  const [phone, setPhone] = useState(initial.phone);
  const [country, setCountry] = useState(initial.country);
  const [email, setEmail] = useState(contact?.email ?? "");
  const [stage, setStage] = useState(contact?.stageId ?? "");
  const [owner, setOwner] = useState(contact?.ownerId ?? (mode === "edit" ? "" : meId)); // у свободного контакта в правке ответственного не подставляем
  const [lifecycleOpen, setLifecycleOpen] = useState(false);
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [company, setCompany] = useState(contact?.company ?? "");
  const [tagIds, setTagIds] = useState<string[]>(contact?.tags.map((t) => t.id) ?? []);
  // свои поля карточки: значения в форме храним строками, к типу приводит сервер
  const [custom, setCustom] = useState<Record<string, string | boolean>>(() =>
    Object.fromEntries(Object.entries(contact?.fields ?? {}).map(([k, v]) => [k, typeof v === "boolean" ? v : Array.isArray(v) ? v.join("; ") : String(v ?? "")])),
  );
  const [allTags, setAllTags] = useState<Tag[]>([]);
  const [groups, setGroups] = useState<TagGroup[]>([]);
  const [defs, setDefs] = useState<ContactFieldDef[]>([]);
  const [identities, setIdentities] = useState<NonNullable<Contact["identities"]>>([]);
  const [client, setClient] = useState<{ company: { id: string; name: string; role: string } | null; customers: ZohoCustomer[] } | null>(null);
  const [deals, setDeals] = useState<Deal[]>([]);
  const box = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    tagsApi.list().then(setAllTags, () => undefined);
    tagsApi.groups().then(setGroups, () => undefined);
    fieldsApi.list().then((list) => setDefs(list.filter((f) => !f.system && f.visibility !== "hidden")), () => undefined);
  }, []);

  /* окно «Новый диалог» во «Входящих» закрыли без отправки — вернуться в эту карточку */
  const backHere = () => {
    if (!contact?.id) return;
    showContact(contact.id);
    goAnchor("#contacts-app");
  };

  // WhatsApp, Telegram и доп. номера есть только в полной карточке
  useEffect(() => {
    if (!contact?.id) return;
    contactsApi.get(contact.id).then((c) => setIdentities(c.identities ?? []), () => undefined);
    zohoApi.ofContact(contact.id).then(setClient, () => undefined);
    dealsApi.list({ contact: contact.id, size: 20 }).then((r) => setDeals(r.items), () => undefined);
  }, [contact?.id]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const mainStages = stages.filter((s) => !s.lost);
  const lostStages = stages.filter((s) => s.lost);
  const selected = stages.find((s) => s.id === stage);
  const dial = contactCountries.find((c) => c.code === country)?.dial ?? DEFAULT_COUNTRY.dial;
  const ready = first.trim().length > 0;
  // владелец может быть отключён — показываем его в списке, чтобы не потерять
  const ownerOptions = owners.some((o) => o.id === owner) || !owner ? owners : [...owners, { id: owner, name: t("Другой пользователь"), role: "" }];

  const save = async () => {
    setTouched(true);
    if (!ready || busy) return;
    const raw = phone.trim();
    const body: ContactInput = {
      firstName: first.trim(),
      lastName: last.trim(),
      // номер с «+» берём как есть, иначе добавляем код выбранной страны; местный ноль («07421…», «056…») отбрасываем —
      // кроме Италии, где ноль входит в городской номер
      phone: raw ? (raw.startsWith("+") ? raw : `${dial}${country === "IT" ? raw : raw.replace(/^[\s0]+/, "")}`) : null,
      email: email.trim() || null,
    };
    if (raw) body.country = country;
    body.company = company.trim();
    body.tagIds = tagIds;
    if (defs.length) {
      body.fields = Object.fromEntries(
        defs.map((f) => {
          const v = custom[f.key];
          if (f.type === "bool") return [f.key, v === true];
          const t = typeof v === "string" ? v.trim() : "";
          return [f.key, f.type === "multi" ? t.split(/[;,]/).map((x) => x.trim()).filter(Boolean) : t || null];
        }),
      );
    }
    if (stage) body.stageId = stage;
    else if (mode === "edit") body.stageId = null;
    if (staff && owner) body.ownerId = owner;
    setBusy(true);
    setError("");
    try {
      const saved = mode === "edit" && contact ? await contactsApi.update(contact.id, body) : await contactsApi.create(body);
      onSaved(saved, mode === "new");
    } catch (err) {
      setError(apiErrorText(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async () => {
    if (!contact || busy) return;
    if (!confirmDelete) {
      setConfirmDelete(true);
      return;
    }
    setBusy(true);
    setError("");
    try {
      await contactsApi.remove(contact.id);
      onDeleted(contact);
    } catch (err) {
      setError(apiErrorText(err));
      setConfirmDelete(false);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="absolute inset-0 z-[60] flex justify-end">
      <button type="button" onClick={onClose} aria-label={t("Закрыть панель")} className="absolute inset-0 cursor-pointer bg-black/45 backdrop-blur-[1px]" />

      <div
        ref={box}
        role="dialog"
        aria-modal="true"
        aria-labelledby="contact-panel-title"
        className="relative flex h-full w-full max-w-[23rem] flex-col border-s border-border bg-card shadow-lift"
      >
        {/* шапка */}
        <div className="flex items-center gap-2 border-b border-border px-3.5 py-3">
          <span className="grid size-7 flex-none place-items-center rounded-btn bg-primary/10 text-primary" aria-hidden>
            <i className="ri-user-add-line text-[14px] leading-none" />
          </span>
          <h4 id="contact-panel-title" className="min-w-0 flex-1 truncate text-[13.5px] font-semibold">
            {mode === "new" ? t("Новый контакт") : t("Контакт")}
          </h4>
          <button
            type="button"
            onClick={onClose}
            aria-label={t("Закрыть панель")}
            className="grid size-7 flex-none cursor-pointer place-items-center rounded-btn text-muted-foreground transition hover:bg-muted hover:text-foreground"
          >
            <X className="size-4" aria-hidden />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-3.5 py-3">
          {/* владелец карточки */}
          <div className="flex items-center justify-between gap-2">
            <p className="text-[10px] font-semibold uppercase tracking-[0.14em] text-muted-foreground">{t("Данные контакта")}</p>
            {mode === "edit" && contact ? (
              <OwnerControl
                kind="contacts"
                id={contact.id}
                owner={owner ? { id: owner, name: owners.find((o) => o.id === owner)?.name ?? t("Другой пользователь") } : null}
                onChanged={(o) => setOwner(o?.id ?? "")}
                compact
              />
            ) : staff ? (
            <div className="relative">
              <select
                value={owner}
                onChange={(e) => setOwner(e.target.value)}
                aria-label={t("Владелец контакта")}
                className="h-7 cursor-pointer appearance-none rounded-btn border border-border bg-background pe-6 ps-2 text-[11px] font-medium text-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
              >
                {ownerOptions.map((o) => (
                  <option key={o.id} value={o.id}>
                    {o.name}
                  </option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
            </div>
            ) : (
              <span className="text-[11px] font-medium text-muted-foreground">{owners.find((o) => o.id === owner)?.name ?? t("Вы")}</span>
            )}
          </div>

          <div className="mt-3 flex flex-col gap-2.5">
            <label className="block">
              <span className="text-[11.5px] font-semibold">
                {t("Имя")} <span className="text-primary">*</span>
              </span>
              <input
                value={first}
                onChange={(e) => setFirst(e.target.value)}
                maxLength={60}
                placeholder={t("Введите имя")}
                autoFocus
                aria-invalid={touched && !ready}
                className={cn(FIELD, "mt-1", touched && !ready && "border-red-500/60 focus:border-red-500 focus:ring-red-500/20")}
              />
              {touched && !ready ? <span className="mt-1 block text-[10.5px] text-red-600">{t("Имя обязательно — без него контакт не создать")}</span> : null}
            </label>

            <label className="block">
              <span className="text-[11.5px] font-semibold">{t("Фамилия")}</span>
              <input value={last} onChange={(e) => setLast(e.target.value)} maxLength={60} placeholder={t("Введите фамилию")} className={cn(FIELD, "mt-1")} />
            </label>

            <div>
              <span className="text-[11.5px] font-semibold">{t("Телефон")}</span>
              <div className="mt-1 flex gap-1.5">
                <div className="relative flex-none">
                  <select
                    value={country}
                    onChange={(e) => setCountry(e.target.value)}
                    aria-label={t("Код страны")}
                    className="h-9 cursor-pointer appearance-none rounded-btn border border-border bg-background pe-6 ps-2 text-[12px] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/25"
                  >
                    {contactCountries.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.flag} {c.dial}
                      </option>
                    ))}
                  </select>
                  <i className="ri-arrow-down-s-line pointer-events-none absolute end-1 top-1/2 -translate-y-1/2 text-[12px] leading-none text-muted-foreground" aria-hidden />
                </div>
                <input
                  value={phone}
                  onChange={(e) => setPhone(e.target.value)}
                  inputMode="tel"
                  maxLength={28}
                  placeholder="50 000 0000"
                  aria-label={t("Номер телефона")}
                  className={cn(FIELD, "min-w-0 flex-1")}
                />
              </div>
            </div>

            {client && (client.company || client.customers.length > 0) ? (
              <div className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
                <span className="text-[11.5px] font-semibold">{t("Клиент")}</span>
                {client.company ? (
                  <button
                    type="button"
                    onClick={() => {
                      showCompany(client.company!.id);
                      goAnchor("#companies");
                    }}
                    className="mt-1 flex w-full cursor-pointer items-center gap-2 text-start text-[12.5px] font-semibold text-primary hover:underline"
                  >
                    <i className="ri-building-2-line leading-none" aria-hidden />
                    <span className="min-w-0 flex-1 truncate">{client.company.name}</span>
                    {client.company.role ? <span className="text-[11px] font-normal text-muted-foreground">{PERSON_ROLES[client.company.role] ?? client.company.role}</span> : null}
                  </button>
                ) : null}
                {client.customers.map((z) => (
                  <p key={z.booksId} className="mt-1 text-[11.5px] text-muted-foreground">
                    Zoho Books: <span className="text-foreground">{z.name}</span> · {aed(z.revenue)}
                    {z.receivable >= 1 ? <span className="font-semibold text-amber-600"> · {t("долг {sum}", { sum: aed(z.receivable) })}</span> : null}
                  </p>
                ))}
                {client.customers.length > 0 ? <FinanceBlock parent={{ contactId: contact!.id }} /> : null}
              </div>
            ) : null}

            {contact?.id ? <WhereFrom ads={contact.ads} first={contact.sourceFirst} origin={contact.source} /> : null}
            {contact?.id && (phone.trim() || email.trim()) ? (
              <div className="flex flex-wrap gap-1.5" data-testid="contact-write">
                {phone.trim() ? (
                  <button
                    type="button"
                    onClick={() => {
                      showNewDialog(phone.trim(), backHere);
                      goAnchor("#inbox-app");
                    }}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-foreground transition hover:bg-muted"
                  >
                    <i className="ri-whatsapp-line text-emerald-600" aria-hidden />
                    {t("Написать в WhatsApp")}
                  </button>
                ) : null}
                {email.trim() ? (
                  <button
                    type="button"
                    onClick={() => {
                      showNewDialog(email.trim(), backHere);
                      goAnchor("#inbox-app");
                    }}
                    className="inline-flex h-8 cursor-pointer items-center gap-1.5 rounded-btn border border-border px-2.5 text-[11.5px] font-semibold text-foreground transition hover:bg-muted"
                  >
                    <i className="ri-mail-send-line text-muted-foreground" aria-hidden />
                    {t("Написать письмо")}
                  </button>
                ) : null}
              </div>
            ) : null}
            {contact?.id ? <ContactDialogs contactId={contact.id} /> : null}

            {contact?.id ? (
              <div className="rounded-btn border border-border bg-background/60 px-3 py-2.5">
                <div className="flex items-center gap-2">
                  <span className="flex-1 text-[11.5px] font-semibold">{t("Сделки")}{deals.length ? ` · ${deals.length}` : ""}</span>
                  <button
                    type="button"
                    onClick={() => {
                      showNewDeal({ contactId: contact.id });
                      goAnchor("#deals");
                    }}
                    className="cursor-pointer text-[11.5px] font-semibold text-primary hover:underline"
                  >
                    + {t("Сделка")}
                  </button>
                </div>
                {deals.map((d) => (
                  <button
                    key={d.id}
                    type="button"
                    onClick={() => {
                      showDeal(d.id);
                      goAnchor("#deals");
                    }}
                    className="mt-1 flex w-full cursor-pointer items-center gap-2 text-start text-[12px] hover:underline"
                  >
                    <span className="size-2 flex-none rounded-full" style={{ background: tagColors.find((c) => c.id === d.stage?.color)?.hex ?? tagColors[0].hex }} aria-hidden />
                    <span className="min-w-0 flex-1 truncate font-semibold">{d.title}</span>
                    <span className="text-[11px] text-muted-foreground">{d.stage?.name}</span>
                    {d.amount !== null ? <span className="text-[11px] tabular-nums text-muted-foreground">{money(d.amount, d.currency)}</span> : null}
                  </button>
                ))}
                {deals.length === 0 ? <p className="mt-1 text-[11.5px] text-muted-foreground">{t("Сделок пока нет.")}</p> : null}
              </div>
            ) : null}
            {contact?.id ? <TasksBlock parent={{ contactId: contact.id }} /> : null}
            {contact?.id ? <NotesBlock parent={{ contactId: contact.id }} /> : null}

            {identities.length > 0 ? (
              <div>
                <span className="text-[11.5px] font-semibold">{t("Другие каналы")}</span>
                <ul className="mt-1 flex flex-col gap-1">
                  {identities.map((i) => (
                    <li key={`${i.kind}-${i.value}`} className="flex items-center gap-2 text-[12px] text-foreground">
                      <i
                        className={cn(
                          "text-sm leading-none",
                          i.kind === "whatsapp" ? "ri-whatsapp-line text-emerald-600" : i.kind === "telegram" ? "ri-telegram-line text-sky-600" : "ri-phone-line text-muted-foreground",
                        )}
                        aria-hidden
                      />
                      <span className="text-muted-foreground">{i.kind === "whatsapp" ? "WhatsApp" : i.kind === "telegram" ? "Telegram" : t("Доп. телефон")}</span>
                      <span className="tabular-nums">{i.kind === "telegram" && !i.value.startsWith("+") ? `@${i.value}` : i.value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}

            <label className="block">
              <span className="text-[11.5px] font-semibold">{t("Электронная почта")}</span>
              <input
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                type="email"
                maxLength={80}
                placeholder={t("Введите адрес почты")}
                className={cn(FIELD, "mt-1")}
              />
            </label>

            <label className="block">
              <span className="text-[11.5px] font-semibold">{t("Компания")}</span>
              <input value={company} onChange={(e) => setCompany(e.target.value)} maxLength={120} placeholder={t("Название компании")} className={cn(FIELD, "mt-1")} />
            </label>

            {/* жизненный цикл */}
            <div>
              <span className="text-[11.5px] font-semibold">{t("Жизненный цикл")}</span>
              <button
                type="button"
                onClick={() => setLifecycleOpen((v) => !v)}
                aria-expanded={lifecycleOpen}
                className="mt-1 flex h-9 w-full box-border cursor-pointer items-center gap-2 rounded-btn border border-border bg-background px-2.5 text-start text-[12.5px] transition hover:border-primary/50"
              >
                {selected ? (
                  <>
                    <span className={cn("grid size-4 flex-none place-items-center rounded-[4px] text-[10px] leading-none", stageTone(selected))} aria-hidden>
                      <i className={selected.icon} />
                    </span>
                    <span className="min-w-0 flex-1 truncate">{selected.name}</span>
                  </>
                ) : (
                  <span className="min-w-0 flex-1 truncate text-muted-foreground/70">{t("Добавить этап жизненного цикла")}</span>
                )}
                {selected ? (
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={t("Сбросить этап")}
                    onClick={(e) => {
                      e.stopPropagation();
                      setStage("");
                    }}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.stopPropagation();
                        setStage("");
                      }
                    }}
                    className="grid size-5 flex-none place-items-center rounded-full text-muted-foreground transition hover:bg-muted hover:text-foreground"
                  >
                    <X className="size-3" aria-hidden />
                  </span>
                ) : null}
                <ChevronToggle open={lifecycleOpen} />
              </button>

              {lifecycleOpen ? (
                <div className="mt-1 max-h-64 overflow-y-auto rounded-btn border border-border bg-card p-1 shadow-lift">
                  <p className="px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">{t("Этапы жизненного цикла")}</p>
                  {mainStages.map((s) => (
                    <StageOption
                      key={s.id}
                      stage={s}
                      active={stage === s.id}
                      onPick={() => {
                        setStage(s.id);
                        setLifecycleOpen(false);
                      }}
                    />
                  ))}
                  {lostStages.length ? (
                    <p className="mt-1 border-t border-border/70 px-2 py-1 text-[9.5px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                      {t("Потерянные этапы")}
                    </p>
                  ) : null}
                  {lostStages.map((s) => (
                    <StageOption
                      key={s.id}
                      stage={s}
                      active={stage === s.id}
                      onPick={() => {
                        setStage(s.id);
                        setLifecycleOpen(false);
                      }}
                    />
                  ))}
                </div>
              ) : null}
            </div>
          </div>

          {/* теги: у группы (как Reporting Tag в Books) — одно значение из списка; простые теги — чипы + список с поиском.
              Новые теги и группы заводятся в окне «Теги» */}
          <div className="mt-3">
            <span className="text-[11.5px] font-semibold">{t("Теги")}</span>
            {groups.map((g) => {
              const opts = allTags.filter((x) => x.groupId === g.id);
              const cur = tagIds.find((id) => opts.some((o) => o.id === id)) ?? "";
              return (
                <label key={g.id} className="mt-1.5 block">
                  <span className="text-[11px] text-muted-foreground">
                    {g.emoji ? <span aria-hidden>{g.emoji} </span> : null}
                    {g.name}
                  </span>
                  <select
                    value={cur}
                    onChange={(e) => setTagIds((prev) => [...prev.filter((id) => !opts.some((o) => o.id === id)), ...(e.target.value ? [e.target.value] : [])])}
                    className={cn(FIELD, "mt-0.5 cursor-pointer")}
                  >
                    <option value="">{t("— не выбрано —")}</option>
                    {opts.map((o) => (
                      <option key={o.id} value={o.id}>
                        {o.name}
                      </option>
                    ))}
                  </select>
                </label>
              );
            })}
            <div className="mt-1.5">
              <TagPicker
                all={allTags.filter((x) => !x.groupId)}
                value={tagIds.filter((id) => allTags.some((x) => x.id === id && !x.groupId))}
                onChange={(ids) => setTagIds((prev) => [...prev.filter((id) => allTags.some((x) => x.id === id && x.groupId)), ...ids])}
              />
            </div>
          </div>

          {/* свои поля из «Полей контактов»; подписи из базы — t() переводит известные (сид, «Из импорта») */}
          {defs.length ? (
            <div className="mt-3 grid gap-3">
              {defs.map((f) =>
                f.type === "bool" ? (
                  <label key={f.id} className="flex cursor-pointer items-center gap-2 text-[11.5px] font-semibold">
                    <input type="checkbox" checked={custom[f.key] === true} onChange={(e) => setCustom((c) => ({ ...c, [f.key]: e.target.checked }))} className="size-4 accent-[hsl(var(--primary))]" />
                    {t(f.label)}
                  </label>
                ) : (
                  <label key={f.id} className="block">
                    <span className="text-[11.5px] font-semibold">
                      {t(f.label)}
                      {f.visibility === "admin" ? <span className="ms-1 font-normal text-muted-foreground">{t("· видно только администраторам")}</span> : null}
                    </span>
                    <input
                      value={typeof custom[f.key] === "string" ? (custom[f.key] as string) : ""}
                      onChange={(e) => setCustom((c) => ({ ...c, [f.key]: e.target.value }))}
                      type={f.type === "date" ? "date" : f.type === "number" ? "text" : f.type === "email" ? "email" : "text"}
                      inputMode={f.type === "number" ? "decimal" : f.type === "phone" ? "tel" : undefined}
                      list={f.type === "multi" && f.options.length ? `opts-${f.id}` : undefined}
                      placeholder={
                        f.type === "multi"
                          ? f.options.length
                            ? t("Например: {list}", { list: f.options.slice(0, 3).join("; ") })
                            : t("Значения через ;")
                          : f.type === "telegram"
                            ? t("+971 50 123 4567 или @nick")
                            : t(f.description || f.label)
                      }
                      className={cn(FIELD, "mt-1")}
                    />
                    {f.type === "multi" && f.options.length ? (
                      <datalist id={`opts-${f.id}`}>
                        {f.options.map((o) => (
                          <option key={o} value={o} />
                        ))}
                      </datalist>
                    ) : null}
                  </label>
                ),
              )}
            </div>
          ) : null}

          <p className="mt-4 rounded-btn border border-border bg-background/50 p-2.5 text-[10.5px] leading-relaxed text-muted-foreground">
            {t("Контакт появится в базе рабочего пространства. Этап можно не выбирать — новый лид получит этап по умолчанию из настроек жизненного цикла.")}
          </p>
        </div>

        {/* подвал */}
        {error ? (
          <p role="alert" className="border-t border-red-500/30 bg-red-500/[.06] px-3.5 py-2 text-[11.5px] text-red-600">
            {error}
          </p>
        ) : null}
        <div className="flex items-center justify-end gap-2 border-t border-border px-3.5 py-2.5">
          {mode === "edit" && staff ? (
            <button
              type="button"
              onClick={remove}
              disabled={busy}
              className={cn(
                "me-auto inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-2.5 text-[12px] font-semibold transition disabled:cursor-wait",
                confirmDelete ? "bg-red-600 text-white hover:brightness-110" : "text-red-600 hover:bg-red-500/10",
              )}
            >
              {confirmDelete ? t("Точно удалить?") : t("Удалить")}
            </button>
          ) : null}
          <button
            type="button"
            onClick={onClose}
            className="inline-flex h-9 cursor-pointer items-center justify-center whitespace-nowrap rounded-btn px-3 text-[12.5px] font-semibold text-muted-foreground transition hover:text-foreground"
          >
            {t("Отмена")}
          </button>
          <button
            type="button"
            onClick={save}
            aria-disabled={!ready || busy}
            className={cn(
              "inline-flex h-9 items-center justify-center gap-1.5 whitespace-nowrap rounded-btn px-4 text-[12.5px] font-semibold transition",
              busy && "cursor-wait opacity-70",
              ready ? "cursor-pointer bg-primary text-primary-foreground hover:brightness-110" : "cursor-not-allowed bg-foreground/10 text-muted-foreground",
            )}
          >
            {busy ? t("Сохраняем…") : mode === "new" ? t("Создать") : t("Сохранить")}
          </button>
        </div>
      </div>
    </div>
  );
}

function ChevronToggle({ open }: { open: boolean }) {
  return <i className={cn("ri-arrow-down-s-line flex-none text-[13px] leading-none text-muted-foreground transition", open && "rotate-180")} aria-hidden />;
}

function StageOption({ stage, active, onPick }: { stage: Stage; active: boolean; onPick: () => void }) {
  return (
    <button
      type="button"
      onClick={onPick}
      aria-pressed={active}
      className={cn(
        "flex w-full cursor-pointer items-center gap-2 rounded-[5px] px-2 py-1.5 text-start text-[12px] transition hover:bg-muted",
        active && "bg-muted font-semibold",
      )}
    >
      <span className={cn("grid size-4 flex-none place-items-center rounded-[4px] text-[10px] leading-none", stageTone(stage))} aria-hidden>
        <i className={stage.icon} />
      </span>
      <span className="min-w-0 flex-1 truncate">{stage.name}</span>
      {active ? <Check className="size-3.5 flex-none text-primary" strokeWidth={3} aria-hidden /> : null}
    </button>
  );
}